import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = [
    "calendar", "cancelLink", "clearButton", "day", "inputs", "refresh",
    "selectedCounter", "selectionSummary", "startDate", "updateButton"
  ]
  static values = { selectedDays: Array }

  connect() {
    this.selected = new Set(this.selectedDaysValue || [])
    this.updateSelection()
  }

  updateButtonTargetConnected() {
    if (this.selected) this.updateActions()
  }

  refresh(event) {
    // Pass the triggering input's name to the server via params[:refresh]
    // so the controller knows which field initiated the form refresh
    this.refreshTarget.value = event.target.name

    this.element.requestSubmit(this.refreshTarget)
  }

  toggleDay(event) {
    const dayEl = event.currentTarget
    const day = dayEl.dataset.day
    if (this.selected.has(day)) {
      this.selected.delete(day)
    } else {
      this.selected.add(day)
    }
    this.updateSelection()
  }

  clearSelection() {
    this.selected.clear()
    this.updateSelection()
  }

  updateSelection() {
    this.updateHighlights()
    this.renderInputs()
    this.updateSelectionSummary()
    this.updateActions()
    this.updateViewedMonth()
  }

  updateHighlights() {
    this.dayTargets.forEach(dayEl => {
      const day = dayEl.dataset.day
      const selected = this.selected.has(day)
      dayEl.setAttribute("aria-pressed", selected.toString())
      if (selected) {
        dayEl.classList.add("selected", "bg-primary", "text-white")
      } else {
        dayEl.classList.remove("selected", "bg-primary", "text-white")
      }
    })
  }

  renderInputs() {
    this.inputsTarget.innerHTML = ""
    this.sortedSelectedDays.forEach(day => {
      const input = document.createElement("input")
      input.type = "hidden"
      input.name = "selected_days[]"
      input.value = day
      this.inputsTarget.appendChild(input)
    })
  }

  updateSelectionSummary() {
    const count = this.selected.size
    this.selectedCounterTarget.textContent = `${count} ${count === 1 ? "date" : "dates"} selected`

    if (count === 0) {
      this.selectionSummaryTarget.textContent = "No dates selected."
      return
    }

    const monthFormatter = new Intl.DateTimeFormat(undefined, {
      month: "long",
      year: "numeric"
    })
    const months = new Map()
    this.sortedSelectedDays.forEach(day => {
      const [year, month, date] = day.split("-").map(Number)
      const monthDate = new Date(year, month - 1, 1)
      const label = monthFormatter.format(monthDate)
      months.set(label, [...(months.get(label) || []), date])
    })

    this.selectionSummaryTarget.textContent = Array.from(months, ([month, dates]) =>
      `${month}: ${dates.join(", ")}`
    ).join(" · ")
  }

  updateActions() {
    const count = this.selected.size
    this.clearButtonTarget.disabled = count === 0
    this.updateButtonTarget.disabled = count === 0
    this.updateButtonTarget.value = `Update ${count} ${count === 1 ? "date" : "dates"}`
  }

  updateViewedMonth() {
    const month = this.calendarTarget.querySelector("time[datetime]")?.dateTime
    if (!month) return

    this.startDateTarget.value = `${month}-01`

    const url = new URL(this.cancelLinkTarget.href)
    const selectedDay = this.sortedSelectedDays.find(day => day.startsWith(`${month}-`))
    url.searchParams.set("start_date", this.startDateTarget.value)
    if (selectedDay) {
      url.searchParams.set("day", selectedDay)
    } else {
      url.searchParams.delete("day")
    }
    this.cancelLinkTarget.href = url.toString()
  }

  get sortedSelectedDays() {
    return Array.from(this.selected).sort()
  }
}
